import { useState, useCallback, useEffect } from 'react'
import * as filestack from 'filestack-js'
import { type FSProgressEvent } from 'filestack-js/build/main/lib/api/upload'

interface FilestackProps {
  value: string | null
  filestackApiKey: string
  onFileUpload?: (uploadUrl: string, file: File, key?: string) => void
}

export const useFilestack = ({ value, filestackApiKey, onFileUpload }: FilestackProps) => {
  const [client, setClient] = useState<filestack.Client>()
  const [url, setUrl] = useState<string | null>(value)
  const [fileName, setFileName] = useState<string | null>(value)
  const [uploading, setUploading] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [uploadError, setUploadError] = useState<boolean>(false)

  useEffect(() => {
    setClient(filestack.init(filestackApiKey))
  }, [filestackApiKey])

  const onUploadProgress = useCallback(
    (e: FSProgressEvent) => setUploadProgress(e.totalPercent),
    [setUploadProgress]
  )

  const uploadFile = useCallback(
    async (file: File, container?: string, region?: string, path?: string) => {
      setUploading(true)
      setUploadError(false)

      try {
        if (client?.upload) {
          const res = await client.upload(
            file,
            {
              onProgress: onUploadProgress,
            },
            { location: 's3', filename: file.name, container, region, path }
          )
          onFileUpload?.(res.url, file, res.key)
          setUrl(res.url)
          setFileName(file.name)
        }
      } catch (_) {
        setUploadError(true)
        setUrl(null)
        setFileName(null)
      }

      setUploading(false)
    },
    [onFileUpload, client, onUploadProgress]
  )

  const clearFile = useCallback(() => {
    setUrl(null)
    setFileName(null)
    setUploading(false)
    setUploadError(false)
    setUploadProgress(0)
  }, [])

  return {
    url,
    fileName,
    uploadFile,
    clearFile,
    uploading,
    uploadProgress,
    uploadError,
  }
}
